import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { Check, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/generals/button'
import { TextField } from '@/components/widgets/quote-form-parts'
import { siteConfig } from '@/constants/site'

export const CallbackForm: FC = (): ReactNode => {
    const [zip, setZip] = useState('')
    const [phone, setPhone] = useState('')
    const [name, setName] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found: Record<string, string> = {}

        if (!/^\d{5}$/.test(zip)) found.zip = "Please enter your 5-digit ZIP code."
        if (phone.replace(/\D/g, '').length < 10) found.phone = "Please enter a phone number we can call."

        setErrors(found)

        if (Object.keys(found).length) return

        setStatus('submitting')

        try {
            const response = await fetch('/api/quote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service: 'emergency',
                    zip,
                    answers: {},
                    urgency: 'Today',
                    visitTime: 'ASAP',
                    contact: { name, phone, email: '', company: '' },
                    notes: 'Callback requested from the emergency page.',
                    items: [],
                    entry: 'callback',
                    page: '/services/emergency-repair',
                    photo: null,
                    website
                })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean } | null

            if (!response.ok || !data?.ok) throw new Error('Request failed')

            setStatus('done')
        }
        catch {
            setStatus('error')
        }
    }

    if (status === 'done') {
        return (
            <div className='flex items-start gap-4 rounded-[24px] bg-moss/10 p-6' role='status'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-moss text-white'>
                    <Check className='size-5' aria-hidden='true' />
                </span>

                <p className='text-[15.5px] leading-relaxed text-ink'>
                    Request received. A technician will ring <span className='font-mono'>{phone}</span> shortly. If it can't wait, call us directly at{' '}
                    <a href={siteConfig.phoneHref} className='font-mono underline underline-offset-2'>{siteConfig.phone}</a>.
                </p>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='grid gap-4 sm:grid-cols-[1fr_1fr_1.2fr_auto] sm:items-start'>
            <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                <label>
                    Website
                    <input tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
                </label>
            </div>

            <TextField
                label="ZIP code"
                value={zip}
                onChange={value => setZip(value.replace(/\D/g, '').slice(0, 5))}
                inputMode='numeric'
                autoComplete='postal-code'
                error={errors.zip}
                maxLength={5}
            />

            <TextField
                label="Your name"
                value={name}
                onChange={setName}
                autoComplete='name'
                optional
            />

            <TextField
                label="Phone number"
                type='tel'
                value={phone}
                onChange={setPhone}
                inputMode='tel'
                autoComplete='tel'
                error={errors.phone}
                maxLength={24}
            />

            <div className='sm:pt-7'>
                <Button
                    type='submit'
                    disabled={status === 'submitting'}
                    icon={status === 'submitting' ? LoaderCircle : 'arrow'}
                    className='w-full justify-between sm:w-auto'
                >
                    {status === 'submitting' ? "Sending" : "Request a callback"}
                </Button>
            </div>

            {status === 'error' && (
                <p role='alert' className='text-[14px] text-ember sm:col-span-4'>
                    Your request didn't reach us. Please call {siteConfig.phone} directly.
                </p>
            )}
        </form>
    )
}
